import { useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  LinearProgress,
  Pagination,
} from '@mui/material';
import { useLocation, useParams } from 'react-router-dom';
import { ProductEmptyState, ProductFilters, MultipleProductSelectionHeader, ProductSelectionMode, type ProductSelectionContext } from '../components';
import { extractErrorMessage } from '../../../utils/errorUtils';
import { CommonList, ListSelectionMode } from '../../common/components';
import { productService } from '../../../services/productService';
import type { Product } from '../../../types/product';
import { useProductData } from '../useProductData';
import { useProductMultiSelection } from './useProductMultiSelection';

const SELECT_ALL_PAGE_SIZE = 5000;

export default function ProductMultiSelectPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const companyId = id ? parseInt(id) : null;

  const { data, pagination, filters } = useProductData({ companyId });

  const selectionContext = location.state as ProductSelectionContext;

  const {
    data: { selectedProductsMap, limitError },
    actions: { handleToggleProductSelection, handleSelectAll, handleClearLimitError },
  } = useProductMultiSelection({
    products: data.products,
    initialProducts: selectionContext?.activityFilters?.products,
    maxSelection: selectionContext?.maxSelection,
  });

  const [selectAllError, setSelectAllError] = useState<string | null>(null);
  const [selectAllInfo, setSelectAllInfo] = useState<string | null>(null);

  const handleSelectAllProducts = async () => {
    if (!companyId) {
      return;
    }

    setSelectAllError(null);
    setSelectAllInfo(null);
    try {
      const response = await productService.search(companyId, {
        name: filters.debouncedName || undefined,
        code: filters.debouncedCode || undefined,
        includeArchived: !filters.showOnlyActive,
        page: 0,
        size: SELECT_ALL_PAGE_SIZE,
      });

      if (!response.last) {
        setSelectAllInfo(`Товарів більше, ніж ${SELECT_ALL_PAGE_SIZE} — обрано лише перші ${SELECT_ALL_PAGE_SIZE}`);
      }

      handleSelectAll(response.content);
    } catch (err) {
      console.error('Error selecting all products:', err);
      setSelectAllError(extractErrorMessage(err, 'Не вдалося вибрати всі товари. Спробуйте ще раз.'));
    }
  };

  if (data.isFirstLoad) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '80vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (data.error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          {data.error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 3 } }}>
      {companyId && (
        <MultipleProductSelectionHeader
          companyId={companyId}
          showFilters={filters.showFilters}
          onToggleFilters={() => filters.setShowFilters(!filters.showFilters)}
          selectedProductsMap={selectedProductsMap}
          hasActiveFilters={!!(filters.filterName || filters.filterCode)}
          returnPath={selectionContext?.returnPath}
          activityFilters={selectionContext?.activityFilters}
          infoText={selectionContext?.infoText}
          onSelectAll={selectionContext?.showSelectAll ? handleSelectAllProducts : undefined}
        />
      )}

      <ProductFilters
        show={filters.showFilters}
        filterName={filters.filterName}
        onFilterNameChange={filters.setFilterName}
        filterCode={filters.filterCode}
        onFilterCodeChange={filters.setFilterCode}
        onClearFilters={filters.handleClearFilters}
      />

      {selectAllError && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setSelectAllError(null)}
        >
          {selectAllError}
        </Alert>
      )}

      {selectAllInfo && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          onClose={() => setSelectAllInfo(null)}
        >
          {selectAllInfo}
        </Alert>
      )}

      {limitError && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          onClose={handleClearLimitError}
        >
          {limitError}
        </Alert>
      )}

      {data.loading && <LinearProgress sx={{ mb: 2 }} />}

      <Box
        sx={{
          opacity: data.loading ? 0.6 : 1,
          transition: 'opacity 0.2s',
        }}
      >
        {data.products.length === 0 && !data.loading ? (
          <ProductEmptyState
            hasFilters={!!(filters.debouncedName || filters.debouncedCode)}
            selectionMode={ProductSelectionMode.MULTIPLE_PRODUCT_SELECTION}
            onCreateProduct={() => {}}
          />
        ) : (
          <>
            <CommonList<Product>
              items={data.products}
              toListItem={(item) => ({
                id: item.id,
                primary: item.name,
                secondary: item.code,
                imageId: item.mainImageId,
              })}
              selectionMode={ListSelectionMode.MULTIPLE_SELECTION}
              selectedItemIds={Array.from(selectedProductsMap.keys())}
              onToggleSelect={handleToggleProductSelection}
              emptyMessage="Продукти не знайдено"
            />

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                mt: 3,
              }}
            >
              <Pagination
                {...pagination}
                color="primary"
                showFirstButton
                showLastButton
              />
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
